import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface TickerSymbol {
  proName: string;
  title?: string;
  description?: string;
}

export interface UserSettings {
  tickerSymbols: TickerSymbol[];
  watchlistSymbols: TickerSymbol[];
  notifications: {
    inBrowserEnabled: boolean;
    pushEnabled: boolean;
  };
  translation: {
    enabled: boolean;
    targetLanguage: string;
    autoTranslate: boolean;
  };
  // Add other settings here in the future
}

const defaultTickerSymbols: TickerSymbol[] = [
  {
    description: "Gold",
    proName: "OANDA:XAUUSD"
  },
  {
    proName: "FOREXCOM:SPXUSD", 
    title: "S&P 500 Index"
  },
  {
    proName: "FOREXCOM:NSXUSD",
    title: "US 100 Cash CFD"
  },
  {
    proName: "FX_IDC:EURUSD",
    title: "EUR to USD"
  },
  {
    proName: "BITSTAMP:BTCUSD",
    title: "Bitcoin"
  }
];

const defaultSettings: UserSettings = {
  tickerSymbols: defaultTickerSymbols,
  watchlistSymbols: [],
  notifications: {
    inBrowserEnabled: true,
    pushEnabled: true,
  },
  translation: {
    enabled: false,
    targetLanguage: 'auto',
    autoTranslate: false,
  },
};

interface UserSettingsContextType {
  settings: UserSettings;
  updateSettings: (newSettings: Partial<UserSettings>) => Promise<void>;
  updateTickerSymbols: (symbols: TickerSymbol[]) => Promise<void>;
  updateWatchlistSymbols: (symbols: TickerSymbol[]) => Promise<void>;
  updateNotificationSettings: (notifications: Partial<UserSettings['notifications']>) => Promise<void>;
  updateTranslationSettings: (translation: Partial<UserSettings['translation']>) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};

interface UserSettingsProviderProps {
  children: ReactNode;
}

export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user settings from Firestore
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentUser) {
        setSettings(defaultSettings);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data() as UserSettings;
          setSettings({
            ...defaultSettings,
            ...data,
          });
        } else {
          // Create default settings document for new user
          await setDoc(doc(db, 'userSettings', currentUser.uid), defaultSettings);
          setSettings(defaultSettings);
        }
      } catch (err) {
        console.error('Error loading user settings:', err);
        setError('Failed to load settings');
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [currentUser]);

  const updateSettings = async (newSettings: Partial<UserSettings>) => {
    if (!currentUser) {
      throw new Error('User must be logged in to update settings');
    }

    try {
      setError(null);
      const updatedSettings = { ...settings, ...newSettings };
      
      await updateDoc(doc(db, 'userSettings', currentUser.uid), newSettings);
      setSettings(updatedSettings);
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Failed to update settings');
      throw err;
    }
  };

  const updateTickerSymbols = async (symbols: TickerSymbol[]) => {
    await updateSettings({ tickerSymbols: symbols });
  };

  const updateWatchlistSymbols = async (symbols: TickerSymbol[]) => {
    await updateSettings({ watchlistSymbols: symbols });
  };

  const updateNotificationSettings = async (notifications: Partial<UserSettings['notifications']>) => {
    const updatedNotifications = { ...settings.notifications, ...notifications };
    await updateSettings({ notifications: updatedNotifications });
  };

  const updateTranslationSettings = async (translation: Partial<UserSettings['translation']>) => {
    const updatedTranslation = { ...settings.translation, ...translation };
    await updateSettings({ translation: updatedTranslation });
  };

  const value: UserSettingsContextType = {
    settings,
    updateSettings,
    updateTickerSymbols,
    updateWatchlistSymbols,
    updateNotificationSettings,
    updateTranslationSettings,
    loading,
    error,
  };

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
}; 